import { EventEmitter } from "events"
import dispatcher from "../dispatcher"
import autobind from "class-autobind"
import AccountStore from "./AccountStore"
import * as hierarchyHelper from "../helpers/hierarchyHelper"
import * as utils from "../helpers/utils"
import { IHierarchyDefinition, IHierarchyItem, IHierarchyItemForAccountLevel } from "../models"

class HierarchySettingsStore extends EventEmitter {
    private filteredItemsList: IHierarchyItem[]
    private nextLevelDefinition: IHierarchyDefinition
    private levelDefinition: IHierarchyDefinition
    private selectedLevelItem: IHierarchyItem
    private selectedLevelItemPathToRoot: IHierarchyItemForAccountLevel[]
    private canSelectedHaveChildren: boolean
    private globalLevelItemFilterId: string
    public globalLevelItemFilter: IHierarchyItem

    constructor() {
        super()
        this.setMaxListeners(0)
        autobind(this)
        AccountStore.on("account_change", this.accountChangeEvent)

        this.filteredItemsList = []
        this.nextLevelDefinition = null
        this.levelDefinition = null
        this.selectedLevelItem = null
        this.selectedLevelItemPathToRoot = []
        this.canSelectedHaveChildren = false
        this.globalLevelItemFilterId = this.getGlobalHierarchyLevelIdCookie()
        this.globalLevelItemFilter = null
    }

    resetData() {
        this.globalLevelItemFilter = null
        this.nextLevelDefinition = null
        this.levelDefinition = null
        this.selectedLevelItem = null
    }

    accountChangeEvent() {
        AccountStore.getCurrentAccount() &&
            this.receiveGlobalSelectedLevelItem(hierarchyHelper.getHierarchyItemById(this.globalLevelItemFilterId))
    }

    getFilteredItems() {
        return this.filteredItemsList
    }

    getSelectedLevelItem() {
        return this.selectedLevelItem
    }

    getNextLevelDefinition() {
        return this.nextLevelDefinition
    }

    getLevelDefinition() {
        return this.levelDefinition
    }

    getSelectedLevelItemPathToRoot() {
        return this.selectedLevelItemPathToRoot
    }

    getCanSelectedHaveChildren() {
        return this.canSelectedHaveChildren
    }

    getGlobalLevelItemFilter() {
        return this.globalLevelItemFilter
    }

    getGlobalLevelItemFilterId() {
        return this.globalLevelItemFilterId ? this.globalLevelItemFilterId : ""
    }

    receiveSelectedLevelItem(selectedLevelItem: IHierarchyItem) {
        this.selectedLevelItem = selectedLevelItem

        this.levelDefinition = null
        if (this.selectedLevelItem === null || !this.selectedLevelItem.Id) {
            //base case, we have a faux level representing the account root.
            this.selectedLevelItem = null
            this.nextLevelDefinition = AccountStore.getCurrentAccount().HierarchyDefinitions[0]
            this.canSelectedHaveChildren = true
        }
        else {
            this.levelDefinition = hierarchyHelper.getHierarchyDefinitionByDefinitionId(this.selectedLevelItem.HierarchyDefinitionId)
            //Get child definition based on level order
            this.nextLevelDefinition = hierarchyHelper.getHierarchyDefinitionByLevel(this.levelDefinition.Level + 1)

            //Is there a next level definition? If not, we are on a leaf definition.
            if (this.nextLevelDefinition && hierarchyHelper.getHierarchyDefinitionByLevel(this.nextLevelDefinition.Level)) {
                this.canSelectedHaveChildren = true
            } else {
                this.canSelectedHaveChildren = false
            }
        }

        this.filteredItemsList = hierarchyHelper.getChildHierarchyItems(this.selectedLevelItem ? this.selectedLevelItem.Id : null)
        this.selectedLevelItemPathToRoot = hierarchyHelper.getHierarchyPathToRoot(this.selectedLevelItem ? this.selectedLevelItem.Id : null)
        this.emit("change")
    }

    receiveGlobalSelectedLevelItem(item) {
        const globalLevelItemFilterId = item ? item.Id : ""
        this.globalLevelItemFilter = item
        this.globalLevelItemFilterId = globalLevelItemFilterId
        this.receiveSelectedLevelItem(item)
        this.emit("global_change")
        //Save level selection to be loaded from cookie on refresh
        this.saveGlobalHierarchyLevelIdCookie(this.globalLevelItemFilterId)
    }

    saveGlobalHierarchyLevelIdCookie(levelId) {
        localStorage.setItem("findd_levelId", levelId)
    }

    getGlobalHierarchyLevelIdCookie() {
        const globalHierarchyLevelId = utils.getCookie("levelId")
        return globalHierarchyLevelId === "null" ? null : globalHierarchyLevelId
    }

    checkHierarchyHasChildHierarchy(hierarchyId: string) {
        return hierarchyId ?
            AccountStore.getCurrentAccount().HierarchyItems.find(i => i.ParentId === hierarchyId) ?
                true :
                false :
            true
    }

    handleActions(action) {
        switch (action.type) {
            case "LOGOUT": {
                this.resetData()
                break
            }
            case "RECEIVE_HIERARCHY_SELECTED_ITEM": {
                this.receiveSelectedLevelItem(action.object)
                break
            }
            case "RECEIVE_GLOBAL_HIERARCHY_SELECTED_ITEM": {
                this.receiveGlobalSelectedLevelItem(action.object)
                break
            }
        }
    }
}

const hierarchySettingsStore = new HierarchySettingsStore()
dispatcher.register(hierarchySettingsStore.handleActions.bind(hierarchySettingsStore))
export default hierarchySettingsStore
